import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Receipt as ReceiptIcon,
  Event as EventIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isAfter, isBefore, parseISO } from 'date-fns';
import {
  getAllContracts,
  createContract,
  updateContract,
  deleteContract,
  Contract
} from '../../../firebase/services/contractService';
import { getAllSlips, Slip } from '../../../firebase/services/slipService';
import { getAllCustomers, Customer, searchCustomers, createCustomer, CustomerInput } from '../../../firebase/services/customerVesselService';
import CustomerSelector from '../../../components/CustomerSelector';

interface ContractFormData {
  slipId: string;
  slipNumber: string;
  customerId: string;
  customerName: string;
  startDate: Date | null;
  endDate: Date | null;
  status: 'active' | 'pending' | 'expired' | 'cancelled';
  paymentStatus: 'paid' | 'pending' | 'overdue';
  amount: number;
  paymentReceivedDate: Date | null;
  paymentMethod: 'cash' | 'credit_card' | 'check' | 'bank_transfer' | 'square' | 'other';
  paymentNotes: string;
  documents: string[];
}

const ContractManagementPage: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [slips, setSlips] = useState<Slip[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [formData, setFormData] = useState<ContractFormData>({
    slipId: '',
    slipNumber: '',
    customerId: '',
    customerName: '',
    startDate: null,
    endDate: null,
    status: 'pending',
    paymentStatus: 'pending',
    amount: 0,
    paymentReceivedDate: null,
    paymentMethod: 'credit_card',
    paymentNotes: '',
    documents: []
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Sort state
  const [sortBy, setSortBy] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsData, slipsData, customersData] = await Promise.all([
        getAllContracts(),
        getAllSlips(),
        getAllCustomers()
      ]);
      setContracts(contractsData);
      setSlips(slipsData);
      setCustomers(customersData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (contract?: Contract) => {
    if (contract) {
      setSelectedContract(contract);
      // Find the customer for this contract
      const customer = customers.find(c => c.id === contract.customerId);
      setSelectedCustomer(customer || null);
      
      setFormData({
        slipId: contract.slipId,
        slipNumber: contract.slipNumber,
        customerId: contract.customerId,
        customerName: contract.customerName,
        startDate: contract.startDate,
        endDate: contract.endDate,
        status: contract.status,
        paymentStatus: contract.paymentStatus,
        amount: contract.amount,
        paymentReceivedDate: contract.paymentReceivedDate || null,
        paymentMethod: contract.paymentMethod || 'credit_card',
        paymentNotes: contract.paymentNotes || '',
        documents: contract.documents
      });
    } else {
      setSelectedContract(null);
      setSelectedCustomer(null);
      setFormData({
        slipId: '',
        slipNumber: '',
        customerId: '',
        customerName: '',
        startDate: new Date(),
        endDate: null,
        status: 'pending',
        paymentStatus: 'pending',
        amount: 0,
        paymentReceivedDate: null,
        paymentMethod: 'credit_card',
        paymentNotes: '',
        documents: []
      });
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedContract(null);
    setSelectedCustomer(null);
    setFormErrors({});
  };

  const handleSlipChange = (slipId: string) => {
    const slip = slips.find(s => s.id === slipId);
    if (slip) {
      setFormData(prev => ({
        ...prev,
        slipId: slip.id,
        slipNumber: slip.slipNumber || ''
      }));
    }
  };

  const handleCustomerSelect = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    if (customer) {
      const customerName = (customer.firstName && customer.lastName) 
        ? `${customer.firstName} ${customer.lastName}`
        : customer.companyName || 'Unnamed Customer';
      
      setFormData(prev => ({
        ...prev,
        customerId: customer.id,
        customerName
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customerId: '',
        customerName: ''
      }));
    }
  };

  const handleCustomerCreate = async (customerData: CustomerInput) => {
    try {
      const newCustomer = await createCustomer(customerData);
      // Refresh customers list
      await fetchData();
      // Select the newly created customer
      setSelectedCustomer(newCustomer);
      const customerName = (newCustomer.firstName && newCustomer.lastName) 
        ? `${newCustomer.firstName} ${newCustomer.lastName}`
        : newCustomer.companyName || 'Unnamed Customer';
      
      setFormData(prev => ({
        ...prev,
        customerId: newCustomer.id,
        customerName
      }));
    } catch (err) {
      console.error('Error creating customer:', err);
      setError('Failed to create customer');
      throw err;
    }
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!formData.slipId) errors.slipId = 'Slip is required';
    if (!formData.customerId) errors.customerId = 'Customer is required';
    if (!formData.startDate) errors.startDate = 'Start date is required';
    if (!formData.endDate) errors.endDate = 'End date is required';
    if (formData.amount < 0) errors.amount = 'Amount must be positive';
    if (formData.startDate && formData.endDate && isAfter(formData.startDate, formData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      
      const contractData = {
        slipId: formData.slipId,
        slipNumber: formData.slipNumber,
        customerId: formData.customerId,
        customerName: formData.customerName,
        startDate: formData.startDate!,
        endDate: formData.endDate!,
        status: formData.status,
        paymentStatus: formData.paymentStatus,
        amount: formData.amount,
        paymentReceivedDate: formData.paymentReceivedDate,
        paymentMethod: formData.paymentMethod,
        paymentNotes: formData.paymentNotes,
        documents: formData.documents
      };

      if (selectedContract) {
        await updateContract(selectedContract.id!, contractData);
      } else {
        await createContract(contractData);
      }

      await fetchData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving contract:', err);
      setError('Failed to save contract');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (contract: Contract) => {
    setSelectedContract(contract);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedContract) return;

    try {
      await deleteContract(selectedContract.id!);
      await fetchData();
      setDeleteDialogOpen(false);
      setSelectedContract(null);
    } catch (err) {
      console.error('Error deleting contract:', err);
      setError('Failed to delete contract');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'default';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'error';
      default: return 'default';
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  const sortContracts = (contracts: Contract[]) => {
    if (!sortBy) return contracts;

    return [...contracts].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'slip':
          aValue = a.slipNumber;
          bValue = b.slipNumber;
          break;
        case 'customer':
          aValue = a.customerName;
          bValue = b.customerName;
          break;
        case 'startDate':
          aValue = new Date(a.startDate);
          bValue = new Date(b.startDate);
          break;
        case 'endDate':
          aValue = new Date(a.endDate);
          bValue = new Date(b.endDate);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'paymentStatus':
          aValue = a.paymentStatus;
          bValue = b.paymentStatus;
          break;
        case 'amount':
          aValue = a.amount;
          bValue = b.amount;
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

      // Compare values
      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.slipNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    const matchesPayment = paymentFilter === 'all' || contract.paymentStatus === paymentFilter;

    return matchesSearch && matchesStatus && matchesPayment;
  });

  const contractsByTab = () => {
    let tabFilteredContracts;
    switch (tabValue) {
      case 0: 
        tabFilteredContracts = filteredContracts;
        break;
      case 1: 
        tabFilteredContracts = filteredContracts.filter(c => c.status === 'active');
        break;
      case 2: 
        tabFilteredContracts = filteredContracts.filter(c => c.paymentStatus === 'pending' || c.paymentStatus === 'overdue');
        break;
      case 3: 
        tabFilteredContracts = filteredContracts.filter(c => c.status === 'expired' || c.status === 'cancelled');
        break;
      default: 
        tabFilteredContracts = filteredContracts;
        break;
    }
    return sortContracts(tabFilteredContracts);
  };

  const displayedContracts = contractsByTab();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Contract Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Contract
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventIcon sx={{ color: 'primary.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total Contracts
                    </Typography>
                    <Typography variant="h5">
                      {contracts.length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <HomeIcon sx={{ color: 'success.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Active Contracts
                    </Typography>
                    <Typography variant="h5">
                      {contracts.filter(c => c.status === 'active').length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ color: 'warning.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Pending Payment
                    </Typography>
                    <Typography variant="h5">
                      {contracts.filter(c => c.paymentStatus === 'pending' || c.paymentStatus === 'overdue').length}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ color: 'info.main', mr: 2 }} />
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total Revenue
                    </Typography>
                    <Typography variant="h5">
                      ${contracts.filter(c => c.paymentStatus === 'paid').reduce((sum, c) => sum + c.amount, 0).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs and Filters */}
        <Paper sx={{ mb: 2 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
              <Tab label="All Contracts" />
              <Tab label="Active" />
              <Tab label="Payment Due" />
              <Tab label="Expired/Cancelled" />
            </Tabs>
          </Box>
          
          <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search contracts..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Payment</InputLabel>
              <Select
                value={paymentFilter}
                label="Payment"
                onChange={(e) => setPaymentFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {/* Contracts Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleSort('slip')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Slip
                    {sortBy === 'slip' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('customer')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Customer
                    {sortBy === 'customer' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('startDate')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Start Date
                    {sortBy === 'startDate' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('endDate')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    End Date
                    {sortBy === 'endDate' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('status')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Status
                    {sortBy === 'status' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('paymentStatus')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Payment Status
                    {sortBy === 'paymentStatus' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('amount')}
                  sx={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Amount
                    {sortBy === 'amount' && (
                      sortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedContracts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contract) => (
                  <TableRow key={contract.id} hover>
                    <TableCell>{contract.slipNumber}</TableCell>
                    <TableCell>{contract.customerName}</TableCell>
                    <TableCell>{format(contract.startDate, 'MM/dd/yyyy')}</TableCell>
                    <TableCell>{format(contract.endDate, 'MM/dd/yyyy')}</TableCell>
                    <TableCell>
                      <Chip
                        label={contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                        color={getStatusColor(contract.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={contract.paymentStatus.charAt(0).toUpperCase() + contract.paymentStatus.slice(1)}
                        color={getPaymentStatusColor(contract.paymentStatus) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>${contract.amount.toLocaleString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit Contract">
                        <IconButton onClick={() => handleOpenDialog(contract)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Contract">
                        <IconButton onClick={() => handleDelete(contract)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={displayedContracts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>

        {/* Contract Form Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedContract ? 'Edit Contract' : 'Create New Contract'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!formErrors.slipId}>
                  <InputLabel>Slip</InputLabel>
                  <Select
                    value={formData.slipId}
                    label="Slip"
                    onChange={(e) => handleSlipChange(e.target.value)}
                  >
                    {slips
                      .filter((slip) => slip.status !== 'occupied')
                      .sort((a, b) => {
                        // Sort by slip number in ascending order
                        const aNum = parseInt(a.slipNumber || '0');
                        const bNum = parseInt(b.slipNumber || '0');
                        return aNum - bNum;
                      })
                      .map((slip) => (
                        <MenuItem key={slip.id} value={slip.id}>
                          Slip {slip.slipNumber} ({slip.status})
                        </MenuItem>
                      ))}
                  </Select>
                  {formErrors.slipId && (
                    <Typography variant="caption" color="error">
                      {formErrors.slipId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box>
                  <CustomerSelector
                    selectedCustomer={selectedCustomer}
                    onCustomerSelect={handleCustomerSelect}
                    onCustomerCreate={handleCustomerCreate}
                  />
                  {formErrors.customerId && (
                    <Typography variant="caption" color="error">
                      {formErrors.customerId}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, startDate: date }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!formErrors.startDate}
                      helperText={formErrors.startDate}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, endDate: date }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!formErrors.endDate}
                      helperText={formErrors.endDate}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={formData.paymentStatus}
                    label="Payment Status"
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentStatus: e.target.value as any }))}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                    <MenuItem value="overdue">Overdue</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  error={!!formErrors.amount}
                  helperText={formErrors.amount}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={formData.paymentMethod}
                    label="Payment Method"
                    onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value as any }))}
                  >
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="check">Check</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="square">Square</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <DatePicker
                  label="Payment Received Date"
                  value={formData.paymentReceivedDate}
                  onChange={(date) => setFormData(prev => ({ ...prev, paymentReceivedDate: date }))}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Payment Notes"
                  value={formData.paymentNotes}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentNotes: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : (selectedContract ? 'Update' : 'Create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the contract for {selectedContract?.customerName} 
              at Slip {selectedContract?.slipNumber}? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default ContractManagementPage; 